<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Button from '@/components/ui/UIButton.vue';
import Badge from '@/components/ui/Badge.vue';
import { Copy, FileJson, X, CheckCircle, History, Braces } from 'lucide-vue-next';
import type { ExecutionResult } from '@/types/ui/PromptManager';
import { basicSetup, EditorView } from 'codemirror';
import { linter } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import { json, jsonParseLinter } from '@codemirror/lang-json';

// ------
// Props
// ------
interface Props {
  result: ExecutionResult | null;
}

const props = defineProps<Props>();

defineEmits<{
  clear: [];
  showHistory: [];
}>();

const copySuccess = ref(false);
const editorRef = ref<HTMLElement | null>(null);
const editorView = ref<EditorView | null>(null);

// -----------
// Lifecycles
// -----------
onUnmounted(() => {
  if (editorView.value) {
    editorView.value.destroy();
    editorView.value = null;
  }
});

watch(editorRef, (ef) => {
  if (ef !== null) {
    const initialDoc = props.result ? JSON.stringify(props.result.output, null, 2) : '';
    const startState = EditorState.create({
      doc: initialDoc,
      extensions: [basicSetup, json(), linter(jsonParseLinter())],
    });

    editorView.value = new EditorView({
      state: startState,
      parent: ef,
    });
  }
});

// Watch for result changes and update the editor content
watch(() => props.result, (newResult) => {
  if (editorView.value) {
    const newDoc = newResult ? JSON.stringify(newResult.output, null, 2) : '';
    editorView.value.dispatch({
      changes: { from: 0, to: editorView.value.state.doc.length, insert: newDoc },
    });
  }
}, { deep: true });

// ---------
// Handlers
// ---------
const handleCopyResult = async () => {
  if (!props.result) return;

  try {
    const jsonString = JSON.stringify(props.result.output, null, 2);
    await navigator.clipboard.writeText(jsonString);
    copySuccess.value = true;

    setTimeout(() => {
      copySuccess.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy result:', err);
  }
};

function handleFormatOutput() {
  const output = editorView.value?.state.doc.toString() ?? '';
  let result = output;
  try {
    result = JSON.stringify(JSON.parse(output), null, 2);
  } catch (e) {
    console.error(e);
  }
  editorView.value?.dispatch({
    changes: { from: 0, to: editorView.value.state.doc.length, insert: result },
  });
}

// ---------
// Computed
// ---------
const stats = computed(() => {
  if (!props.result?.output) return null;

  const jsonString = JSON.stringify(props.result.output);
  return {
    characters: jsonString.length,
    lines: jsonString.split('\n').length,
    keys: Object.keys(props.result.output).length,
  };
});
</script>
<template>
  <Card class="tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="tw:flex tw:items-center tw:justify-between">
        <div class="tw:flex tw:items-center tw:gap-2">
          <FileJson class="tw:h-5 tw:w-5 tw:text-emerald-600" />
          Results
        </div>
        <div class="tw:flex tw:gap-2">
          <Button variant="outline" size="sm" @click="$emit('showHistory')" title="View prompt execution history">
            <History class="tw:h-4 tw:w-4" />
            History
          </Button>
          <div v-if="result" class="tw:flex tw:gap-2">
            <Button
              variant="outline"
              size="sm"
              @click="handleCopyResult"
              :class="copySuccess ? 'tw:border-green-300 tw:bg-green-50' : ''">
              <template v-if="copySuccess">
                <CheckCircle class="tw:mr-2 tw:h-4 tw:w-4 tw:text-green-600" />
                Copied!
              </template>
              <template v-else>
                <Copy class="tw:h-4 tw:w-4" />
                Copy
              </template>
            </Button>
            <Button variant="outline" size="sm" @click="$emit('clear')">
              <X class="tw:h-4 tw:w-4" />
              Clear
            </Button>
          </div>
        </div>
      </CardTitle>
      <CardDescription> View and copy structured data extracted from documents </CardDescription>
    </CardHeader>
    <CardContent class="tw:space-y-4">
      <template v-if="result">
        <!-- Result Stats -->
        <div
          v-if="stats"
          class="tw:flex tw:items-center tw:gap-4 tw:rounded-lg tw:border tw:border-emerald-200 tw:bg-emerald-50 tw:p-3">
          <Badge variant="secondary"> {{ stats.keys }} keys </Badge>
          <Badge variant="secondary">
            {{ stats.characters }} chars
          </Badge>
          <Badge variant="secondary"> {{ stats.lines }} lines </Badge>
        </div>

        <!-- JSON Display -->
        <div class="tw:space-y-2">
          <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Extracted Data</h4>
          <div ref="editorRef"></div>
        </div>

        <!-- Result Actions -->
        <div class="tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:bg-slate-50 tw:p-3">
          <span class="tw:text-sm tw:text-slate-600"> Execution completed successfully </span>
          <div class="tw:flex tw:gap-2">
            <Button variant="outline" size="sm" @click="handleFormatOutput">
              <Braces class="tw:h-4 tw:w-4" />
              Format JSON
            </Button>
          </div>
        </div>
      </template>
      <div v-else class="tw:py-12 tw:text-center">
        <FileJson class="tw:mx-auto tw:mb-4 tw:h-12 tw:w-12 tw:text-slate-300" />
        <h3 class="tw:mb-2 tw:text-lg tw:font-medium tw:text-slate-700">No Results Yet</h3>
        <p class="tw:mx-auto tw:max-w-sm tw:text-sm tw:text-slate-500">
          Execute a prompt on a document to see the extracted structured data here. Results will be displayed in JSON
          format.
        </p>
      </div>
    </CardContent>
  </Card>
</template>
